/**
 * MobileAsk — single-question research-chat screen (SLICE #7c-2, surface A).
 *
 * One question in, one cited answer out — a single round-trip to the research-chat
 * edge function's additive `stream:false` branch (via askResearch), rather than the
 * SSE stream the desktop surface consumes.
 *
 * Offline behaviour (this is a LIVE LLM call — it CANNOT work offline):
 *  • when `useIsOnline()` is false the input + send button are disabled with a clear
 *    "Ask needs a connection" hint,
 *  • the LAST answer is cached (setCached/getCached "mobile.ask.last") and shown
 *    read-only so the screen is never blank offline,
 *  • we NEVER queue questions — an offline question is simply not sendable.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { History, Send, WifiOff } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useIsOnline } from "@/hooks/useIsOnline";
import { getCached, setCached } from "@/lib/offline/persistentCache";
import { askResearch, stripSourceMarkers, type AskCitation } from "@/lib/ask/askResearch";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

const LAST_ANSWER_CACHE_KEY = "mobile.ask.last";
// Local-only Q&A history (same IndexedDB substrate as the last-answer cache). Tapping an entry replays
// its cached answer — no network. Bounded so the device never accumulates an unbounded transcript.
const HISTORY_CACHE_KEY = "mobile.ask.history";
const HISTORY_MAX = 10;

interface CachedAsk {
  question: string;
  answer: string;
  citations: AskCitation[];
  answeredAt: string;
}

export default function MobileAsk() {
  const online = useIsOnline();
  const { toast } = useToast();

  const [question, setQuestion] = useState("");
  const [busy, setBusy] = useState(false);
  const [current, setCurrent] = useState<CachedAsk | null>(null);
  // True when `current` was restored from the persistent cache (not asked this session).
  const [fromCache, setFromCache] = useState(false);
  // Local-only recent-questions list (most-recent first), restored from + persisted to IndexedDB.
  const [history, setHistory] = useState<CachedAsk[]>([]);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Restore the last answer + recent-questions history on mount so the screen is never blank (esp.
  // offline) and the user can replay any of their last ~10 answers.
  useEffect(() => {
    let active = true;
    (async () => {
      const [lastEnv, histEnv] = await Promise.all([
        getCached<CachedAsk>(LAST_ANSWER_CACHE_KEY),
        getCached<CachedAsk[]>(HISTORY_CACHE_KEY),
      ]);
      if (!active || !mounted.current) return;
      if (lastEnv?.data?.answer) {
        setCurrent(lastEnv.data);
        setFromCache(true);
      }
      if (Array.isArray(histEnv?.data)) {
        setHistory(histEnv.data.filter((h) => h && typeof h.answer === "string").slice(0, HISTORY_MAX));
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const trimmed = question.trim();
  const canSend = online && !busy && trimmed.length > 0;

  const submit = useCallback(async () => {
    const q = question.trim();
    if (!q || busy) return;
    if (!online) {
      toast({
        title: "Ask needs a connection",
        description: "Reconnect to put a new question to the research hub.",
      });
      return;
    }
    setBusy(true);
    try {
      const res = await askResearch(q);
      if (!mounted.current) return;
      const entry: CachedAsk = {
        question: q,
        answer: res.answer,
        citations: res.citations,
        answeredAt: new Date().toISOString(),
      };
      setCurrent(entry);
      setFromCache(false);
      setQuestion("");
      // Prepend to the local recent-questions history (dedupe by question text, cap at HISTORY_MAX).
      setHistory((prev) => {
        const next = [
          entry,
          ...prev.filter((h) => h.question.trim() !== entry.question.trim()),
        ].slice(0, HISTORY_MAX);
        // Best-effort persist; never blocks the UI.
        void setCached(HISTORY_CACHE_KEY, next);
        return next;
      });
      // Best-effort cache of the last answer for offline replay; never blocks the UI.
      void setCached(LAST_ANSWER_CACHE_KEY, entry);
    } catch (e: any) {
      if (!mounted.current) return;
      toast({
        title: "Couldn't answer that",
        description: e?.message || "Something went wrong answering that. Try again.",
        variant: "destructive",
      });
    } finally {
      if (mounted.current) setBusy(false);
    }
  }, [question, busy, online, toast]);

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      // Cmd/Ctrl+Enter sends (Enter alone keeps newline behaviour for longer questions).
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault();
        void submit();
      }
    },
    [submit],
  );

  // Display body: strip `[source: uuid]` markers (the citations list shows them instead).
  const answerBody = useMemo(
    () => (current ? stripSourceMarkers(current.answer) : ""),
    [current],
  );

  // Replay a previously-answered question from local history — purely local, works offline.
  const replay = useCallback((entry: CachedAsk) => {
    setCurrent(entry);
    setFromCache(true);
    setQuestion("");
    // Move the replayed entry to the front so the most-recently-viewed bubbles up.
    setHistory((prev) => {
      const next = [
        entry,
        ...prev.filter((h) => h.question.trim() !== entry.question.trim()),
      ].slice(0, HISTORY_MAX);
      void setCached(HISTORY_CACHE_KEY, next);
      return next;
    });
    void setCached(LAST_ANSWER_CACHE_KEY, entry);
  }, []);

  return (
    <div className="min-h-screen bg-background px-4 pb-8 pt-[calc(1.25rem+env(safe-area-inset-top))]">
      <h1 className="font-heading text-2xl font-semibold text-primary">Ask</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Put a single question to research and get a cited answer.
      </p>

      {/* Composer */}
      <div className="mt-4 rounded-xl border border-border bg-card p-4">
        <label htmlFor="ask-input" className="sr-only">
          Your question
        </label>
        <Textarea
          id="ask-input"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={!online || busy}
          placeholder={
            online
              ? "e.g. What did we conclude on European utilities?"
              : "Ask needs a connection"
          }
          className="min-h-[96px] resize-none bg-background"
        />

        {!online ? (
          <div className="mt-3 flex items-center gap-2 rounded-lg bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
            <WifiOff className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span>Ask needs a connection — questions can't be queued.</span>
          </div>
        ) : null}

        <div className="mt-3 flex items-center justify-end">
          <Button type="button" onClick={() => void submit()} disabled={!canSend}>
            {busy ? (
              <>
                <span
                  className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
                  aria-hidden="true"
                />
                Asking…
              </>
            ) : (
              <>
                <Send className="h-4 w-4" aria-hidden="true" />
                Ask
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Answer */}
      {current ? (
        <div className="mt-4 rounded-xl border border-border bg-card p-4">
          <div className="flex items-center justify-between gap-2">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground/70">
              Answer
            </p>
            {fromCache ? (
              <span className="text-[11px] uppercase tracking-wide text-muted-foreground/70">
                Last answer
              </span>
            ) : null}
          </div>

          {current.question ? (
            <p className="mt-2 text-sm font-medium text-foreground/90">
              {current.question}
            </p>
          ) : null}

          {answerBody ? (
            <div
              className={cn(
                "prose prose-sm mt-3 max-w-none font-body",
                "prose-headings:font-heading prose-a:text-primary",
              )}
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{answerBody}</ReactMarkdown>
            </div>
          ) : (
            <p className="mt-3 text-sm text-muted-foreground">
              No answer text was returned.
            </p>
          )}

          {fromCache && !online ? (
            <p className="mt-3 text-xs text-muted-foreground">
              Showing your last answer — reconnect to ask again.
            </p>
          ) : null}

          {current.citations.length > 0 ? (
            <div className="mt-4 border-t border-border pt-3">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground/70">
                Sources
              </p>
              <ol className="mt-2 space-y-2">
                {current.citations.map((c, i) => (
                  <li key={`${c.source_id}-${i}`} className="text-sm">
                    <span className="mr-1 text-muted-foreground">[{i + 1}]</span>
                    <span className="text-foreground/90">
                      {c.title || "Source"}
                    </span>
                    {c.axis ? (
                      <span className="ml-2 text-[11px] uppercase tracking-wide text-muted-foreground/70">
                        {c.axis}
                      </span>
                    ) : null}
                    <span className="mt-0.5 block break-all text-[11px] text-muted-foreground/60">
                      {c.source_id}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          ) : null}
        </div>
      ) : (
        <div className="mt-4 rounded-xl border border-border bg-card p-4">
          <p className="text-sm text-muted-foreground">
            {online
              ? "Your answer will appear here."
              : "No previous answer is cached on this device."}
          </p>
        </div>
      )}

      {/* Recent questions — local-only history; tap to replay a cached answer (works offline). The
          entry currently on screen is omitted to avoid showing it twice. */}
      {history.filter((h) => h.question.trim() !== current?.question.trim()).length > 0 ? (
        <div className="mt-4">
          <div className="mb-2 flex items-center gap-1.5 px-1">
            <History className="h-3.5 w-3.5 text-muted-foreground/70" aria-hidden="true" />
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground/70">
              Recent questions
            </p>
          </div>
          <ul className="space-y-2">
            {history
              .filter((h) => h.question.trim() !== current?.question.trim())
              .map((h, i) => {
                let when: string | null = null;
                try {
                  when = formatDistanceToNow(new Date(h.answeredAt), { addSuffix: true });
                } catch {
                  when = null;
                }
                return (
                  <li key={`${h.answeredAt}-${i}`}>
                    <button
                      type="button"
                      onClick={() => replay(h)}
                      className="flex w-full flex-col items-start gap-0.5 rounded-xl border border-border bg-card p-3 text-left transition-colors active:bg-muted/50"
                    >
                      <span className="line-clamp-2 text-sm font-medium text-foreground/90">
                        {h.question}
                      </span>
                      {when ? (
                        <span className="text-[11px] uppercase tracking-wide text-muted-foreground/60">
                          {when}
                        </span>
                      ) : null}
                    </button>
                  </li>
                );
              })}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
